import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTheme, useMediaQuery, List } from '@mui/material';

import { useBrandsOrder } from '../../hooks';
import { ConfirmDialog } from '../ConfirmDialog';

import { BrandsOrderDialogProps } from './BrandsOrderDialog.types';
import { BrandOrderTitle } from './BrandOrderTitle';
import { BrandOrderListItem } from './BrandOrderListItem';
import { BrandOrderDialogActions } from './BrandsOrderDialogActions';
import { DescriptionText, StyledDialog, StyledDialogContent, StyledSearchField } from './BrandsOrderDialog.styles';
import { MobileHint } from './MobileHint';
import { getBrandUpdates, getFilteredBrands, getOrderedBrands } from './helpers';

export const BrandOrderDialog = ({ open, scope, context, availableBrands, onClose }: BrandsOrderDialogProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [localOrder, setLocalOrder] = useState<number[]>([]);
  const [visibleBrands, setVisibleBrands] = useState<Set<number>>(new Set());
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const [confirmResetOpen, setConfirmResetOpen] = useState(false);

  const {
    brandsOrder,
    loading,
    regionalBrandsOrder,
    regionalLoading,
    updateRegionalBrandsOrder,
    updateBrandsOrder,
    resetOrder: resetBrandsOrder,
    resetRegionalOrder
  } = useBrandsOrder(open, scope, onClose);

  useEffect(() => {
    if (open) {
      setSearchTerm('');
    }
  }, [open]);

  const isRegional = scope === 'regional';

  const activeBrandsOrder = isRegional ? regionalBrandsOrder : brandsOrder;
  const activeLoading = isRegional ? regionalLoading : loading;
  const updateOrder = isRegional ? updateRegionalBrandsOrder : updateBrandsOrder;
  const resetOrder = isRegional ? resetRegionalOrder : resetBrandsOrder;
  const title = isRegional ? '  Налаштування брендів (регіональні)' : 'Налаштування брендів';
  const description = isRegional
    ? 'Оберіть бренди для відображення в регіональних та обласних таблицях'
    : 'Оберіть бренди для відображення та встановіть порядок їх розміщення';
  const modalMessage = isRegional
    ? 'Ви дійсно хочете скинути всі регіональні налаштування порядку брендів до стандартних?'
    : 'Ви дійсно хочете скинути всі налаштування порядку брендів до стандартних?';

  useEffect(() => {
    if (!open) return;
    if (activeBrandsOrder.length > 0) {
      const orderedBrands = getOrderedBrands(activeBrandsOrder);
      const selectedBrands = activeBrandsOrder.filter(b => b.isSelected);
      setLocalOrder(orderedBrands);
      setVisibleBrands(new Set(selectedBrands.map(b => b.id)));
    }
  }, [activeBrandsOrder, open]);

  const handleSave = () => {
    const brandUpdates = getBrandUpdates(activeBrandsOrder, visibleBrands, localOrder);
    updateOrder(brandUpdates, context);
    onClose();
  };

  const handleReset = () => {
    setConfirmResetOpen(true);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setSearchTerm(e.target.value);
  };

  const confirmReset = () => {
    setConfirmResetOpen(false);
    resetOrder(context);
  };

  const moveUp = (brandId: number) => {
    const currentIndex = localOrder.indexOf(brandId);
    if (currentIndex > 0) {
      const newOrder = [...localOrder];
      [newOrder[currentIndex - 1], newOrder[currentIndex]] = [newOrder[currentIndex], newOrder[currentIndex - 1]];
      setLocalOrder(newOrder);
    }
  };

  const moveDown = (brandId: number) => {
    const currentIndex = localOrder.indexOf(brandId);
    if (currentIndex < localOrder.length - 1) {
      const newOrder = [...localOrder];
      [newOrder[currentIndex], newOrder[currentIndex + 1]] = [newOrder[currentIndex + 1], newOrder[currentIndex]];
      setLocalOrder(newOrder);
    }
  };

  const handleBrandToggle = (brandId: number) => {
    const newVisible = new Set(visibleBrands);
    if (newVisible.has(brandId)) {
      newVisible.delete(brandId);
      setLocalOrder(prev => prev.filter(id => id !== brandId));
    } else {
      newVisible.add(brandId);
      if (!localOrder.includes(brandId)) {
        setLocalOrder(prev => [...prev, brandId]);
      }
    }
    setVisibleBrands(newVisible);
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    e.dataTransfer.setData('text/plain', index.toString());
    e.dataTransfer.effectAllowed = 'move';
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    if (draggedIndex !== null && draggedIndex !== index) {
      const newOrder = [...localOrder];
      const draggedBrand = newOrder[draggedIndex];
      newOrder.splice(draggedIndex, 1);
      newOrder.splice(index, 0, draggedBrand);
      setLocalOrder(newOrder);
      setDraggedIndex(index);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDraggedIndex(null);
  };

  const getBrandName = useCallback(
    (id: number) => {
      return (
        activeBrandsOrder.find(b => b.id === id)?.name || availableBrands.find(b => b.id === id)?.name || `Brand ${id}`
      );
    },
    [activeBrandsOrder, availableBrands]
  );

  const filteredBrands = useMemo(
    () =>
      getFilteredBrands({
        searchTerm,
        visibleBrands,
        localOrder,
        activeBrandsOrder,
        getBrandName
      }),
    [searchTerm, visibleBrands, localOrder, activeBrandsOrder, getBrandName]
  );

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      isMobile={isMobile}
      maxWidth={isMobile ? false : 'sm'}
      fullWidth={!isMobile}
      fullScreen={isMobile}
    >
      <BrandOrderTitle isMobile={isMobile} title={title} onClose={onClose} />

      <StyledDialogContent isMobile={isMobile}>
        <DescriptionText variant="body2" color="text.secondary" isMobile={isMobile}>
          {description}
          <MobileHint isMobile={isMobile} />
        </DescriptionText>

        <StyledSearchField
          fullWidth
          size="small"
          placeholder="Пошук брендів..."
          value={searchTerm}
          onChange={handleSearchChange}
        />

        <List>
          {filteredBrands.selected.map((brandId, index) => {
            const originalIndex = localOrder.indexOf(brandId);
            const isVisible = visibleBrands.has(brandId);
            const isOkko = getBrandName(brandId) === 'ОККО';
            const isDragging = draggedIndex === index;
            return (
              <BrandOrderListItem
                key={brandId}
                type="selected"
                isMobile={isMobile}
                brandId={brandId}
                brandName={getBrandName(brandId)}
                isDragging={isDragging}
                isVisible={isVisible}
                isOkko={isOkko}
                checked={visibleBrands.has(brandId)}
                originalIndex={originalIndex}
                localOrder={localOrder}
                moveUp={moveUp}
                moveDown={moveDown}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onBrandToggle={handleBrandToggle}
              />
            );
          })}
        </List>

        {filteredBrands.unselected.map(brand => (
          <BrandOrderListItem
            isMobile={isMobile}
            key={`${brand.id}_${brand.name}`}
            type="unselected"
            brandId={brand.id}
            brandName={brand.name}
            checked={visibleBrands.has(brand.id)}
            onBrandToggle={handleBrandToggle}
          />
        ))}
      </StyledDialogContent>

      <BrandOrderDialogActions
        isMobile={isMobile}
        loading={activeLoading}
        onReset={handleReset}
        onSave={handleSave}
        onClose={onClose}
      />

      <ConfirmDialog
        open={confirmResetOpen}
        title="Підтвердження скидання"
        message={modalMessage}
        confirmText="Скинути"
        onConfirm={confirmReset}
        onCancel={() => setConfirmResetOpen(false)}
        severity="warning"
      />
    </StyledDialog>
  );
};
